import { OnePagerContext } from './context';
import { OnePagerSectionConfig, OnePagerSubSectionConfig, OnePagerSubSectionId } from '../editor/dealSummaryEditorInitializer';
import {
    DATA_NOT_AVAILABLE_HIGHLIGHT as YELLOW,
    DATA_NOT_AVAILABLE_LABEL as DataNotAvl,
    DEFAULT_TWO_COL_1_2_SIZES,
    MUTED_TEXT_COLOR,
} from '../editor/dealSummaryEditorConstants';
import { bullet, h2, p, spacer, table, td, text, TextNode, TrRow, tr } from './plateNodes';

const KNOWN_DEAL_SUMMARY_SUBSECTION_IDS: readonly OnePagerSubSectionId[] = [
    'legalEntityName',
    'dateOfIncorporation',
    'constitution',
    'sectorIndustry',
    'headquarters',
    'businessOverview',
    'previousInvestorsAndFundingStage',
    'gstCustomersVendors',
];

const DEFAULT_DEAL_SUMMARY_SUBSECTIONS: OnePagerSubSectionConfig[] = [
    { id: 'legalEntityName', visible: true, label: 'Legal Entity Name', order: 1 },
    { id: 'dateOfIncorporation', visible: true, label: 'Date of incorporation', order: 2 },
    { id: 'constitution', visible: true, label: 'Constitution', order: 3 },
    { id: 'sectorIndustry', visible: true, label: 'Sector/Industry', order: 4 },
    { id: 'headquarters', visible: true, label: 'Headquarters', order: 5 },
    { id: 'businessOverview', visible: true, label: 'Business Overview', order: 6 },
    { id: 'previousInvestorsAndFundingStage', visible: true, label: 'Previous Investors and Funding Stage', order: 7 },
    { id: 'gstCustomersVendors', visible: true, label: 'GST Customers and Vendors', order: 8 },
];

// Label/value row: label cell + a single bold value cell (or yellow DataNotAvl).
const labelValueRow = (label: string, value: TextNode): TrRow => tr([td(label), td([value])]);

export function buildDealSummarySection(ctx: OnePagerContext, section: OnePagerSectionConfig): any[] | null {
    const { orgInfo, ai_profile_main_data, ai_profile_main_data_v2, topFiveRevenueSource, topFiveVendorExpense, formData } = ctx;
    const subsectionsConfig = section.subsections ?? null;

    const buildLegalEntityName = (label: string): TrRow =>
        labelValueRow(
            label,
            orgInfo?.orgName
                ? text(`${orgInfo?.orgName ?? ''}`, { bold: true })
                : text(` ${DataNotAvl}`, { backgroundColor: YELLOW, bold: true }),
        );

    const buildDateOfIncorporation = (label: string): TrRow =>
        labelValueRow(
            label,
            ai_profile_main_data?.foundationDate
                ? text(`${ai_profile_main_data?.foundationDate ?? ''}`, { bold: true })
                : text(`${DataNotAvl}`, { backgroundColor: YELLOW, bold: true }),
        );

    const buildConstitution = (label: string): TrRow =>
        labelValueRow(
            label,
            orgInfo?.constitutionType
                ? text(`${orgInfo?.constitutionType ?? ''}`, { bold: true })
                : text(`${DataNotAvl}`, { backgroundColor: YELLOW, bold: true }),
        );

    const buildSectorIndustry = (label: string): TrRow =>
        labelValueRow(
            label,
            formData?.['borrowerProfile.companySector'] || ai_profile_main_data_v2?.data?.sectorDetails?.sector
                ? text(
                      `${
                          formData?.['borrowerProfile.companySector']
                              ? formData?.['borrowerProfile.companySector']
                              : ai_profile_main_data_v2.data.sectorDetails.sector
                      }`,
                      { bold: true },
                  )
                : text(`${DataNotAvl}`, { backgroundColor: YELLOW, bold: true }),
        );

    const buildHeadquarters = (label: string): TrRow =>
        labelValueRow(
            label,
            ai_profile_main_data?.location
                ? text(`${ai_profile_main_data?.location ?? ''}`, { bold: true })
                : text(`${DataNotAvl}`, { backgroundColor: YELLOW, bold: true }),
        );

    const buildBusinessOverview = (label: string): TrRow =>
        labelValueRow(
            label,
            ai_profile_main_data_v2?.data?.overview?.overviewContent
                ? text(ai_profile_main_data_v2.data.overview.overviewContent)
                : text(`${DataNotAvl}`, { backgroundColor: YELLOW }),
        );

    const buildPreviousInvestorsAndFundingStage = (label: string): TrRow =>
        tr([
            td(label),
            td(
                bullet([
                    [
                        text(`Investor Name(s): `, { bold: true }),
                        ai_profile_main_data_v2?.data?.overview?.keyInvestors?.length
                            ? text(ai_profile_main_data_v2.data.overview.keyInvestors.map((investor: any) => investor).join(', '))
                            : text(`${DataNotAvl}`, { backgroundColor: YELLOW }),
                    ],
                    [
                        text(`Funding Stage: `, { bold: true }),
                        ai_profile_main_data?.fundingInfo?.stage
                            ? text(`${ai_profile_main_data?.fundingInfo?.stage ?? ''}`)
                            : text(`${DataNotAvl}`, { backgroundColor: YELLOW }),
                    ],
                ]),
            ),
        ]);

    const buildGstCustomersVendors = (label: string): TrRow => {
        const revenueItems: TextNode[][] = topFiveRevenueSource?.length
            ? topFiveRevenueSource.map((item) => [text(`${item.name}  `), text(item.amountDisplay, { color: MUTED_TEXT_COLOR })])
            : [[text(DataNotAvl, { backgroundColor: YELLOW })]];
        const vendorItems: TextNode[][] = topFiveVendorExpense?.length
            ? topFiveVendorExpense.map((item) => [text(`${item.name}  `), text(item.amountDisplay, { color: MUTED_TEXT_COLOR })])
            : [[text(DataNotAvl, { backgroundColor: YELLOW })]];

        return tr([
            td(label),
            td([
                p([text('Top 5 Customers ', { bold: true }), text('(revenue in last 12 months)', { color: MUTED_TEXT_COLOR })]),
                ...bullet(revenueItems),
                spacer(),
                p([text('Top 5 Vendors ', { bold: true }), text('(expense in last 12 months)', { color: MUTED_TEXT_COLOR })]),
                ...bullet(vendorItems),
            ]),
        ]);
    };

    const ROW_BUILDERS: Record<OnePagerSubSectionId, (label: string) => TrRow> = {
        legalEntityName: buildLegalEntityName,
        dateOfIncorporation: buildDateOfIncorporation,
        constitution: buildConstitution,
        sectorIndustry: buildSectorIndustry,
        headquarters: buildHeadquarters,
        businessOverview: buildBusinessOverview,
        previousInvestorsAndFundingStage: buildPreviousInvestorsAndFundingStage,
        gstCustomersVendors: buildGstCustomersVendors,
    };

    const resolvedSubsections =
        Array.isArray(subsectionsConfig) && subsectionsConfig.length ? subsectionsConfig : DEFAULT_DEAL_SUMMARY_SUBSECTIONS;

    const orderedRows = resolvedSubsections
        .filter((s) => s?.visible && KNOWN_DEAL_SUMMARY_SUBSECTION_IDS.includes(s.id))
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

    const builtRows = orderedRows.map((sub) => ROW_BUILDERS[sub.id](sub.label || sub.id));

    if (!builtRows.length) return null;

    return [h2([text(section.label)]), table(builtRows, { colSizes: DEFAULT_TWO_COL_1_2_SIZES }), spacer()];
}
